import { WuiHyperlink } from '@jwplayer/wui-react';
import { differenceInDays, differenceInHours } from 'date-fns';
import { EXTERNAL_URLS } from '/config/constants';
import { useSession } from '/global-state/session/hooks';
import { usePermissions } from '/module/permissions';
import { Link } from '/module/routing/link';
import css from '../style.module.scss';

export function UpgradeBanner() {
  const { accountHas, accountState, hasTrialExpired, isAdsStopped, isStorageStopped, isStreamingStopped } =
    usePermissions();
  const session = useSession();
  const playerEdition = session.player_edition;

  const canUpgrade = accountHas('can_see_upgrade_page') && accountHas('self_service_upgrades');

  const hasFreePlayerEdition = playerEdition === 'free';
  const hasTrialPlayerEdition = playerEdition === 'trial';
  const hasAccountSuspended = accountState === 'suspended';

  if (isStorageStopped || isAdsStopped || isStreamingStopped) {
    return (
      <Banner
        description="One of your limits has been reached. You can upgrade to increase your limits or wait until they reset again for the month."
        bannerColor="red"
        hasUsageLink
        hasUpgradeButton
      />
    );
  }

  if (hasFreePlayerEdition && canUpgrade && !hasAccountSuspended) {
    return (
      <Banner
        description="You are currently on a Free pricing plan. Upgrade to gain access to our advanced features."
        bannerColor="blue"
        hasUpgradeButton
      />
    );
  }

  if (hasAccountSuspended && canUpgrade) {
    return (
      <Banner
        description="Your account has been downgraded to the JW Player Free edition."
        bannerColor="red"
        hasUpgradeButton
      />
    );
  }

  if (hasTrialExpired) {
    return (
      <Banner
        description="Your Enterprise Trial has expired and your account will be downgraded shortly."
        bannerColor="orange"
      />
    );
  }

  if (hasTrialPlayerEdition && session.subscription_expiration) {
    return (
      <Banner description={generateExpirationText(new Date(session.subscription_expiration))} bannerColor="orange" />
    );
  }

  return null;
}

function Banner({
  description,
  bannerColor,
  hasUsageLink,
  hasUpgradeButton,
}: {
  description: string;
  bannerColor: 'blue' | 'red' | 'orange';
  hasUsageLink?: boolean;
  hasUpgradeButton?: boolean;
}) {
  return (
    <div className={`${css.banner} ${css[bannerColor]}`}>
      <div>
        <span className="color__white">{description}</span>

        {hasUsageLink && (
          <Link to="accountUsage">
            <u className="color__white">See Usage</u>
          </Link>
        )}
      </div>

      {hasUpgradeButton && (
        <WuiHyperlink
          data-test="link-to-sales"
          label="Upgrade now"
          type="inverted"
          href={EXTERNAL_URLS.salesContactForm}
        />
      )}
    </div>
  );
}

function generateExpirationText(subscriptionExpiration: Date) {
  let expirationText: string;

  const elapsedDaysForTrial = subscriptionExpiration && differenceInDays(subscriptionExpiration, new Date());
  const elapsedHoursForTrial = subscriptionExpiration && differenceInHours(subscriptionExpiration, new Date());

  if (elapsedDaysForTrial >= 1) {
    expirationText = `in ${elapsedDaysForTrial} day${elapsedDaysForTrial > 1 && 's'}`;
  } else if (elapsedDaysForTrial < 1 && elapsedHoursForTrial > 0) {
    expirationText = `in ${elapsedHoursForTrial} hour${elapsedHoursForTrial > 1 && 's'}`;
  } else {
    expirationText = 'within the hour';
  }

  const expirationDateMessage = `You are using an Enterprise Trial which expires ${expirationText}. If you require an extension, please reach out to your sales representative.`;
  return expirationDateMessage;
}
